import { createAction, createAsyncThunk } from '@reduxjs/toolkit';

import { Organizer } from '../../domain/interfaces/organizer';
import { UpsertOrganizerInput } from '../../domain/interfaces/upsert-organizer-input';
import _ from 'lodash';
import { backgroundColors } from 'shared/constants/backgrounds';
import i18n from 'i18n';
import organizerRepository from '../../domain/repositories/organizer-repository';
import organizerResource from '../../domain/resources/organizer-resource';
import supabase from '../../../core/supabase/supabase';
import { toast } from 'react-toastify';
import { addTournaments } from 'modules/tournaments/store/available-tournaments';

const PREFIX = 'organizer';

export const fetchOrganizer = createAsyncThunk<Organizer | null, { tournamentId: number }>(
  `${PREFIX}/fetchOrganizer`,
  async (payload, { rejectWithValue }) => {
    try {
      const results = await Promise.allSettled([
        organizerRepository.fetchOrganizer(payload.tournamentId),
        organizerRepository.fetchAvailableFeature(payload.tournamentId),
        organizerRepository.fetchActivePaidSubscription(payload.tournamentId),
        supabase.organizerLogoBacking.get({ tournamentId: payload.tournamentId }),
      ]);

      const [organizerResult, featuresResult, planResult, backgroundResult] = results;

      if (organizerResult.status === 'rejected') {
        console.error(`${PREFIX}/fetchOrganizer error:`, organizerResult.reason);
        return rejectWithValue('Failed to fetch organizer data');
      }

      const organizer = organizerResult.value;
      if (!organizer) return null;

      organizer.availableFeatures =
        featuresResult.status === 'fulfilled'
          ? featuresResult.value
          : {
              MEDIA_MANAGER: false,
              ACTIVE_CHAMPIONSHIPS: false,
              JOYSTICK: false,
              OBS_INTEGRATION: false,
            };
      organizer.tournamentPlan = planResult.status === 'fulfilled' ? planResult.value : null;

      organizer.backgroundColor =
        backgroundResult.status === 'fulfilled' ? backgroundColors[backgroundResult?.value?.type ?? 0] : backgroundColors[0];

      if (featuresResult.status === 'rejected') {
        console.error(`${PREFIX}/fetchFeatures error:`, featuresResult.reason);
      }
      if (planResult.status === 'rejected') {
        console.error(`${PREFIX}/fetchPlan error:`, planResult.reason);
      }
      if (backgroundResult.status === 'rejected') {
        console.error(`${PREFIX}/fetchBackground error:`, backgroundResult.reason);
      }

      return organizer;
    } catch (error) {
      console.error(`${PREFIX}/fetchOrganizers error:`, error);
      return rejectWithValue(error instanceof Error ? error.message : String(error));
    }
  },
);

export const upsertOrganizer = createAsyncThunk<Organizer, UpsertOrganizerInput>(
  `${PREFIX}/upsertOrganizer`,
  async (payload, { dispatch }) => {
    const { id, name, description, phone, email, address, sportType, adminUserId, logo, background } = payload;

    try {
      let organizer = await organizerRepository.upsertOrganizer({
        id,
        name,
        description,
        phone,
        email,
        address,
        sportType,
        adminUserId,
      });

      dispatch(
        addTournaments([
          {
            id: organizer.id,
            name: organizer.name,
            sportType: organizer.sportType,
          },
        ]),
      );

      let backgroundUpdateResult = false;

      if (background) {
        const backgroundType = parseInt(_.findKey(backgroundColors, background) ?? '');
        if (!isNaN(backgroundType) && organizer.id) {
          const result = await supabase.organizerLogoBacking.update({ tournamentId: organizer.id, type: backgroundType });
          backgroundUpdateResult = result;
        }
      }

      if (logo) {
        await organizerResource.uploadLogo(organizer.id, logo);
        const updated = await organizerRepository.fetchOrganizer(organizer.id);
        if (updated) {
          organizer = updated;
        }
      }

      toast.success(i18n.t('settings.saveSettingsSuccess'));

      return { ...organizer, backgroundColor: backgroundUpdateResult ? background : undefined };
    } catch (err) {
      console.error(`${PREFIX}/upsertOrganizer error:`, err);
      toast.error(i18n.t('settings.saveSettingsInternalError'));

      throw err;
    }
  },
);

export const resetOrganizer = createAction(`${PREFIX}/resetOrganizer`);
